import type { CRMUser, EmployeeDesignation, ExecutiveBriefingProfile } from '../types';
import type {
  ApprovalGateId,
  PurchaseApprovalStep,
  PurchaseCostObjectType,
  PurchaseSpendClass,
  PurchasingAuthorityBand,
  PurchasingPolicy,
} from '../types/purchasing';
import { isCooDesignation } from './employeeDesignations';
import { isOrgChartApex } from './orgChart';
import { canOverridePurchaseApproval, type PurchasingActorAccess } from './purchasingAccess';
import { APPROVAL_GATE_LABELS, formatBandLabel, gatesForBand, matchPurchasingBand } from './purchasingPolicy';

export interface ApprovalResolverUser {
  uid: string;
  displayName: string;
  title?: string;
  isActive?: boolean;
  role?: string;
  reportsToUserId?: string;
  designations?: EmployeeDesignation[] | string[];
  /** QA logins must not become the company COO / CFO / CEO approver. */
  isTestUser?: boolean;
  executiveBriefing?: { enabled?: boolean; profile?: ExecutiveBriefingProfile | null };
}

export interface ResolveApprovalChainInput {
  requesterId: string;
  amount: number;
  costType: PurchaseCostObjectType;
  /** CapEx uses finance routing, not the overhead supervisor / COO chain. */
  spendClass?: PurchaseSpendClass;
  projectManagerUserId?: string | null;
  users: ApprovalResolverUser[];
  policy: PurchasingPolicy;
  /** Designer-invoice POs — skip the COO even if they are also the assigned PM. */
  skipCooApprovers?: boolean;
}

export interface ResolvedApprovalChain {
  band: PurchasingAuthorityBand | null;
  bandLabel: string;
  steps: PurchaseApprovalStep[];
  unresolved: boolean;
  selfAuthorized?: boolean;
  error?: string;
}

function displayNameOf(user: ApprovalResolverUser | undefined, fallback = 'Unknown'): string {
  return (user?.displayName || '').trim() || fallback;
}

function isActiveInternal(user: ApprovalResolverUser | undefined): boolean {
  if (!user || user.isActive === false) return false;
  const role = String(user.role || '').toLowerCase();
  return (
    role === 'employee'
    || role === 'admin'
    || role === 'staff'
    || role === 'employee_shop'
    || role === 'employee_office'
    || !user.role
  );
}

function findById(users: ApprovalResolverUser[], uid: string | null | undefined): ApprovalResolverUser | undefined {
  if (!uid) return undefined;
  return users.find(u => u.uid === uid);
}

export function findSupervisorUser(
  users: ApprovalResolverUser[],
  requesterId: string,
): ApprovalResolverUser | undefined {
  const requester = findById(users, requesterId);
  return findById(users, requester?.reportsToUserId);
}

function isAssignableSeat(user: ApprovalResolverUser | undefined): boolean {
  return isActiveInternal(user) && user?.isTestUser !== true;
}

export function findControllerUser(users: ApprovalResolverUser[]): ApprovalResolverUser | undefined {
  return users.find(
    u => isAssignableSeat(u) && (u.designations || []).includes('cfo'),
  );
}

export function findExecutiveUser(users: ApprovalResolverUser[]): ApprovalResolverUser | undefined {
  return users.find(u => {
    if (!isAssignableSeat(u)) return false;
    return isCooDesignation({
      designations: u.designations,
      briefingProfile: u.executiveBriefing?.enabled ? u.executiveBriefing.profile : u.executiveBriefing?.profile,
    });
  });
}

/** Owner / CEO — used for CapEx when the ops COO must not be the approver. */
export function findCeoUser(users: ApprovalResolverUser[]): ApprovalResolverUser | undefined {
  const ceo = users.find(u => isAssignableSeat(u) && requesterIsCeo(u));
  if (ceo) return ceo;
  const titled = users.find(u => isAssignableSeat(u) && isOrgChartApex({ title: u.title }));
  if (titled) return titled;
  return users.find(u => isAssignableSeat(u) && String(u.role || '').toLowerCase() === 'admin');
}

/** True if `ancestorId` sits above `descendantId` when walking `reportsToUserId`. */
export function isOrgChartAncestor(
  users: ApprovalResolverUser[],
  ancestorId: string,
  descendantId: string,
): boolean {
  if (!ancestorId || !descendantId || ancestorId === descendantId) return false;
  const seen = new Set<string>();
  let cur: string | undefined = findById(users, descendantId)?.reportsToUserId;
  while (cur) {
    if (cur === ancestorId) return true;
    if (seen.has(cur)) return false;
    seen.add(cur);
    cur = findById(users, cur)?.reportsToUserId;
  }
  return false;
}

/** Prior approver already satisfies the executive seat (org-chart ancestor or admin over a non-admin COO). */
export function approverOutranksExecutive(
  prior: ApprovalResolverUser,
  executive: ApprovalResolverUser,
  users: ApprovalResolverUser[],
): boolean {
  if (prior.uid === executive.uid) return true;
  if (isOrgChartAncestor(users, prior.uid, executive.uid)) return true;
  const priorAdmin = String(prior.role || '').toLowerCase() === 'admin';
  const execAdmin = String(executive.role || '').toLowerCase() === 'admin';
  return priorAdmin && !execAdmin;
}

function priorStepOutranksExecutive(
  steps: PurchaseApprovalStep[],
  executive: ApprovalResolverUser,
  users: ApprovalResolverUser[],
): boolean {
  return priorStepOutranksUser(steps, executive, users);
}

/** Org-chart seniority, CEO, or admin already covers this approver seat. */
export function approverOutranksUser(
  senior: ApprovalResolverUser,
  junior: ApprovalResolverUser,
  users: ApprovalResolverUser[],
): boolean {
  if (senior.uid === junior.uid) return true;
  if (requesterIsCeo(senior)) return true;
  if (isOrgChartAncestor(users, senior.uid, junior.uid)) return true;
  return approverOutranksExecutive(senior, junior, users);
}

export function priorStepOutranksUser(
  steps: PurchaseApprovalStep[],
  candidate: ApprovalResolverUser,
  users: ApprovalResolverUser[],
): boolean {
  return steps.some(step => {
    const prior = findById(users, step.userId);
    return !!prior && approverOutranksUser(prior, candidate, users);
  });
}

/** Job POs should not send CEO / admin / CFO through a project-manager gate. */
function leadershipSkipsProjectManagerGate(requester: ApprovalResolverUser | undefined): boolean {
  if (!requester) return false;
  if (requesterIsCeo(requester)) return true;
  if (String(requester.role || '').toLowerCase() === 'admin') return true;
  return (requester.designations || []).includes('cfo');
}

function resolveGateUser(
  gate: ApprovalGateId,
  input: ResolveApprovalChainInput,
): ApprovalResolverUser | undefined {
  const capex = input.spendClass === 'capex';
  switch (gate) {
    case 'supervisor':
      // CapEx is not an ops reporting-line purchase.
      if (capex) return undefined;
      return findSupervisorUser(input.users, input.requesterId);
    case 'project_manager':
      if (capex) return undefined;
      return findById(input.users, input.projectManagerUserId);
    case 'controller':
      return findControllerUser(input.users);
    case 'executive':
      return capex ? findCeoUser(input.users) : findExecutiveUser(input.users);
    case 'department_head':
      return findById(input.users, input.policy.fallbackApproverUserId);
    default:
      return undefined;
  }
}

/** COO can issue immediately only through this amount (inclusive). */
export const COO_SELF_AUTHORIZE_MAX_USD = 500;

function requesterBriefingProfile(user: ApprovalResolverUser | undefined): ExecutiveBriefingProfile | null | undefined {
  if (!user?.executiveBriefing) return undefined;
  return user.executiveBriefing.enabled
    ? user.executiveBriefing.profile
    : user.executiveBriefing.profile;
}

export function requesterIsCoo(user: ApprovalResolverUser | undefined): boolean {
  if (!user) return false;
  return isCooDesignation({
    designations: user.designations,
    briefingProfile: requesterBriefingProfile(user),
  });
}

export function isSelfAuthorizedPurchase(row: { approvalSteps?: Array<{ roleLabel?: string }> }): boolean {
  return (row.approvalSteps || []).some(step => step.roleLabel === 'Self-authorized');
}

export function requesterIsCeo(user: ApprovalResolverUser | undefined): boolean {
  if (!user) return false;
  if (requesterBriefingProfile(user) === 'ceo') return true;
  if (isOrgChartApex({ title: user.title })) return true;
  return /\bowner\b/i.test(String(user.title || ''));
}

/**
 * CEO / owner POs never wait on another approver, at any amount or spend class.
 * Admin and CFO also issue immediately. COO is capped.
 */
export function canSelfAuthorizePurchase(
  users: ApprovalResolverUser[],
  requesterId: string,
  fallbackApproverUserId?: string,
  amount?: number,
): boolean {
  const requester = findById(users, requesterId);
  if (!requester || requester.isActive === false) return false;
  if (requesterIsCeo(requester)) return true;
  if (!isActiveInternal(requester)) return false;
  if (String(requester.role || '').toLowerCase() === 'admin') return true;
  if ((requester.designations || []).includes('cfo')) return true;
  if (requesterIsCoo(requester)) {
    if (amount == null) return true;
    return amount <= COO_SELF_AUTHORIZE_MAX_USD + 0.005;
  }
  if (fallbackApproverUserId && fallbackApproverUserId === requesterId) return true;
  return !requester.reportsToUserId && users.some(u => u.reportsToUserId === requesterId);
}

function selfAuthorizedChain(
  band: PurchasingAuthorityBand,
  gates: ApprovalGateId[],
  requester: ApprovalResolverUser,
): ResolvedApprovalChain {
  const gate = gates.includes('executive') ? 'executive' : gates[0] || 'executive';
  return {
    band,
    bandLabel: formatBandLabel(band),
    steps: [
      {
        ...toStep(gate, requester),
        roleLabel: 'Self-authorized',
      },
    ],
    unresolved: false,
    selfAuthorized: true,
  };
}

function toStep(gate: ApprovalGateId, user: ApprovalResolverUser): PurchaseApprovalStep {
  return {
    gate,
    roleLabel: APPROVAL_GATE_LABELS[gate],
    userId: user.uid,
    userName: displayNameOf(user),
    status: 'pending',
  };
}

/** Act on a gate assigned to someone else (leadership override or org-chart seniority). */
export function canActOnPendingPurchaseStep(
  users: ApprovalResolverUser[],
  actorId: string,
  step: PurchaseApprovalStep | undefined,
  access: PurchasingActorAccess,
): boolean {
  if (!step || step.status !== 'pending') return false;
  if (step.userId === actorId) return true;
  if (canOverridePurchaseApproval(access)) return true;
  const actor = findById(users, actorId);
  const assignee = findById(users, step.userId);
  if (actor && assignee && approverOutranksUser(actor, assignee, users)) return true;
  return false;
}

export function purchaseApprovalUsesSubstitutedActor(
  step: PurchaseApprovalStep | undefined,
  actorId: string,
  users: ApprovalResolverUser[],
  access: PurchasingActorAccess,
): boolean {
  if (!step || step.userId === actorId) return false;
  return canActOnPendingPurchaseStep(users, actorId, step, access);
}

export function finalizePurchaseApprovalStep(
  step: PurchaseApprovalStep,
  status: Extract<PurchaseApprovalStep['status'], 'approved' | 'returned' | 'rejected' | 'skipped'>,
  actor: { id: string; name: string },
  opts: { note?: string; decidedAt?: Date; substituteActor?: boolean },
): PurchaseApprovalStep {
  const now = opts.decidedAt || new Date();
  const decided: PurchaseApprovalStep = {
    ...step,
    status,
    decidedAt: now,
    note: opts.note?.trim() || undefined,
  };
  if (!opts.substituteActor || actor.id === step.userId) return decided;
  return {
    ...decided,
    userId: actor.id,
    userName: actor.name.trim() || actor.name || 'Staff',
  };
}

/**
 * Resolve the stored approval path at submit time.
 * Skips missing people and the requester when someone else can approve. Dedupes
 * a person so they only appear once. Skips the executive gate when an earlier
 * approver already outranks that executive on the org chart (or is admin).
 * Empty chains fall back to the policy fallback approver, then to
 * self-authorization for org-apex / leadership.
 */
export function resolveApprovalChain(input: ResolveApprovalChainInput): ResolvedApprovalChain {
  const band = matchPurchasingBand(input.policy, input.amount);
  if (!band) {
    return {
      band: null,
      bandLabel: '',
      steps: [],
      unresolved: true,
      error: 'No delegation-of-authority band matches this amount.',
    };
  }

  const gates = gatesForBand(band, input.costType, input.spendClass);
  const requester = findById(input.users, input.requesterId);

  // CEO / owner issues immediately at any amount — never route to a
  // supervisor, COO, or anyone else. Admin / CFO (and COO under the auto-issue
  // cap) also self-authorize.
  if (
    requester &&
    canSelfAuthorizePurchase(
      input.users,
      input.requesterId,
      input.policy.fallbackApproverUserId,
      input.amount,
    )
  ) {
    return selfAuthorizedChain(band, gates, requester);
  }

  const seen = new Set<string>();
  const steps: PurchaseApprovalStep[] = [];

  for (const gate of gates) {
    const user = resolveGateUser(gate, input);
    if (!user || !isActiveInternal(user)) continue;
    if (user.uid === input.requesterId) continue;
    if (seen.has(user.uid)) continue;
    // Never ask someone below the requester (or a COO when the requester is already above them).
    if (requester && isOrgChartAncestor(input.users, input.requesterId, user.uid)) continue;
    if ((input.spendClass === 'capex' || input.skipCooApprovers) && requesterIsCoo(user)) continue;
    if (gate === 'executive' && requester && approverOutranksExecutive(requester, user, input.users)) continue;
    if (gate === 'executive' && priorStepOutranksExecutive(steps, user, input.users)) continue;
    if (gate === 'project_manager') {
      if (leadershipSkipsProjectManagerGate(requester)) continue;
      if (requester && approverOutranksUser(requester, user, input.users)) continue;
      if (priorStepOutranksUser(steps, user, input.users)) continue;
    }
    seen.add(user.uid);
    steps.push(toStep(gate, user));
  }

  if (steps.length === 0) {
    const fallback = findById(input.users, input.policy.fallbackApproverUserId);
    if (
      fallback
      && isActiveInternal(fallback)
      && fallback.uid !== input.requesterId
      && !(input.skipCooApprovers && requesterIsCoo(fallback))
    ) {
      steps.push(toStep('department_head', fallback));
    }
  }

  if (steps.length === 0) {
    if (
      requester &&
      canSelfAuthorizePurchase(
        input.users,
        input.requesterId,
        input.policy.fallbackApproverUserId,
        input.amount,
      )
    ) {
      return selfAuthorizedChain(band, gates, requester);
    }

    const controller = findControllerUser(input.users);
    if (controller && controller.uid !== input.requesterId && !seen.has(controller.uid)) {
      steps.push(toStep('controller', controller));
      return {
        band,
        bandLabel: formatBandLabel(band),
        steps,
        unresolved: false,
        selfAuthorized: false,
      };
    }

    return {
      band,
      bandLabel: formatBandLabel(band),
      steps: [],
      unresolved: true,
      error:
        requester && requesterIsCoo(requester)
          ? `COO auto-approval stops at $${COO_SELF_AUTHORIZE_MAX_USD.toLocaleString('en-US')}. Add a CFO as controller, or a fallback approver in Settings, for larger POs.`
          : 'Could not resolve an approver. Set a manager on the org chart, assign a project manager, or pick a fallback approver in Settings.',
    };
  }

  return {
    band,
    bandLabel: formatBandLabel(band),
    steps,
    unresolved: false,
    selfAuthorized: false,
  };
}

export function currentPendingStep(steps: PurchaseApprovalStep[]): PurchaseApprovalStep | undefined {
  return steps.find(s => s.status === 'pending');
}

export function currentPendingStepIndex(steps: PurchaseApprovalStep[]): number {
  return steps.findIndex(s => s.status === 'pending');
}

export function crmUsersToResolverUsers(users: CRMUser[]): ApprovalResolverUser[] {
  return users.map(u => ({
    uid: u.uid,
    displayName: u.displayName || u.email || 'Unnamed',
    title: u.title,
    isActive: u.isActive,
    role: u.role,
    reportsToUserId: u.reportsToUserId,
    designations: u.designations,
    isTestUser: u.isTestUser === true,
    executiveBriefing: u.executiveBriefing,
  }));
}

/** Prefer the signed-in profile so CEO briefing / role is not missed if the directory row is stale. */
export function overlaySignedInApprover(
  users: ApprovalResolverUser[],
  signedIn: ApprovalResolverUser | null | undefined,
): ApprovalResolverUser[] {
  if (!signedIn?.uid) return users;
  const existing = users.find(u => u.uid === signedIn.uid);
  const merged: ApprovalResolverUser = existing
    ? {
        ...existing,
        displayName: signedIn.displayName?.trim() || existing.displayName,
        title: existing.title || signedIn.title,
        role: signedIn.role || existing.role,
        designations: signedIn.designations?.length ? signedIn.designations : existing.designations,
        executiveBriefing: signedIn.executiveBriefing || existing.executiveBriefing,
        reportsToUserId: existing.reportsToUserId ?? signedIn.reportsToUserId,
        isActive: signedIn.isActive ?? existing.isActive,
      }
    : { ...signedIn };
  if (!existing) return [...users, merged];
  return users.map(u => (u.uid === merged.uid ? merged : u));
}
